
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const LoadingSpinner = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
    <div className="text-center p-4">
      <div className="w-16 h-16 mx-auto mb-4">
        <img 
          src="/lovable-uploads/98a9fcc4-c199-4be7-8bad-6756a3aa8870.png"
          alt="Loading"
          className="w-full h-full object-contain animate-pulse"
        />
      </div>
      <h2 className="text-xl font-semibold mb-2">Redirecting to Dashboard...</h2>
      <p className="text-gray-500">Please wait while we take you to your service dashboard</p>
    </div>
  </div>
);

export const VendorRedirectInterceptor = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isVendor, isVendorLoading, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const performVendorRedirect = async () => {
      if (!isAuthenticated || !user || !isVendor || isVendorLoading) {
        return;
      }

      console.log("[VendorInterceptor] Redirecting vendor to dashboard:", user.id);

      try {
        // Get the first service for this vendor
        const { data: services, error } = await supabase
          .from('services')
          .select('id')
          .eq('owner_id', user.id)
          .limit(1);

        if (error) {
          console.error('[VendorInterceptor] Error fetching services:', error);
          return;
        }

        if (services && services.length > 0) {
          const serviceUrl = `/service/${services[0].id}/calendar`;
          console.log("[VendorInterceptor] Redirecting to service dashboard:", serviceUrl);
          navigate(serviceUrl, { replace: true });
        } else {
          console.log("[VendorInterceptor] Vendor has no services, staying on current page");
        }
      } catch (error) {
        console.error('[VendorInterceptor] Exception during redirect:', error);
      }
    };

    // Only attempt redirect if auth is fully loaded and user is confirmed as vendor
    if (!isLoading && !isVendorLoading && isAuthenticated && isVendor) {
      performVendorRedirect();
    }
  }, [isAuthenticated, isVendor, isVendorLoading, isLoading, user, navigate]);

  // Show loading while determining vendor status
  if (isAuthenticated && (isLoading || isVendorLoading)) {
    return <LoadingSpinner />;
  }

  // Show loading while redirecting vendors
  if (isAuthenticated && isVendor && !isVendorLoading) {
    return <LoadingSpinner />;
  }

  // For non-vendors or unauthenticated users, render children normally
  return <>{children}</>;
};
